import { useState, useEffect } from 'react'
import { isNotNil } from '../../../shared/lib/functional/nil.no-deps'
import { timeout } from '../../../shared/lib/timeout.no-deps'
import { type PaginatedLoadingConfig, usePaginatedLoading } from './paginatedLoading'

export type InfiniteLoadingConfig<T extends { id: any }> = { onItemsLoad?: () => void } & PaginatedLoadingConfig<T>
export type InfiniteLoaderReference = React.RefObject<{ reset: () => void }>

export const useInfiniteLoading = <T extends { id: any }>(config: InfiniteLoadingConfig<T>) => {
    const {
        items,
        isLoading,
        error,
        loadNextPage,
        setPageToLoad,
        reloadCurrentPage,
        resetPaginatedLoading,
        hasNextPage,
        updateItems,
    } = usePaginatedLoading({
        initialPageNumber: config.initialPageNumber,
        initialNumberOfPagesToLoad: config.initialNumberOfPagesToLoad,
        getItems: config.getItems,
    })

    const [elementTriggeringLoadWhenVisible, setElementTriggeringLoadWhenVisible] = useState<Element | null>(null)

    const reset = () => {
        setElementTriggeringLoadWhenVisible(null)
        resetPaginatedLoading()
    }

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries: IntersectionObserverEntry[]) => {
                const entry = entries[0]
                if (entry && entry.isIntersecting && !isLoading && hasNextPage) {
                    setElementTriggeringLoadWhenVisible(null)
                    if (error.isJust()) {
                        timeout(3000).then(() => reloadCurrentPage()) // this seems like a bad idea
                    } else {
                        loadNextPage()
                    }
                    if (isNotNil(config.onItemsLoad)) {
                        config.onItemsLoad()
                    }
                }
            },
            { threshold: 0 }
        )
        if (isNotNil(elementTriggeringLoadWhenVisible)) {
            observer.observe(elementTriggeringLoadWhenVisible)
        }
        return () => {
            if (isNotNil(elementTriggeringLoadWhenVisible)) {
                observer.unobserve(elementTriggeringLoadWhenVisible)
            }
        }
    }, [elementTriggeringLoadWhenVisible, loadNextPage, hasNextPage, isLoading])

    return {
        items,
        isLoading,
        error,
        setElementTriggeringLoadWhenVisible,
        setPageToLoad,
        loadNextPage,
        reset,
        updateItems,
    }
}
